// Hlavní menu — BYZNYSOVÁ DATA baseline (native) položek (žádné typy ani výkonná logika).
//
// Účel: JEDINÉ místo, které deklaruje, CO se v hlavním menu zobrazuje — které native agendy, kam
// v sekci/skupině patří, v jakém pořadí, s jakým oprávněním a kterou legacy agendu nahrazují.
// Dělení dle účelu (menu concern žije v `app/layout`):
//   - TYPY + factory položek: `mainMenuItems.ts`,
//   - DATA (tento soubor),
//   - LOGIKA slučování native ↔ legacy sekcí: `mergeNativeIntoSections.ts`.

import type { TFunction } from 'i18next';

import { getRuntimeConfig } from '@/shared/runtimeConfig';
import {
  VENUA_PLANNER_PERMISSION,
  VENUA_PLANNER_PLACEHOLDERS,
  VENUA_PLANNER_PLACEHOLDER_SUBCATEGORIES,
  COLUMBUS_PERMISSION,
} from '@/modules/products';
import { PRODUCTS_STATIONS, PRODUCTS_STATION_CHAINS } from '@/modules/products/permissions';
import { INVENTORY_AIRLINES, INVENTORY_VEHICLES } from '@/modules/inventory/permissions';
import { SECURITY_ROLE_PERMISSIONS } from '@/modules/security/permissions';
import { RESERVATIONS_BUSINESS_CASES } from '@/modules/reservations/permissions';
import { WWW_WEB_FILTERS } from '@/modules/www/permissions';

import { standardMenuItem, type MainMenuItem } from './mainMenuItems';

/**
 * Stabilní, na jazyku NEZÁVISLÁ ID sekcí — musí se shodovat s `<SubItem ID>` v Magic
 * `Admin/ObjectStructureMenu.xml`. Podle nich se native agendy párují do legacy sekcí nezávisle
 * na kultuře (dřív se páralo přes lokalizovaný label → při rozjeté kultuře se menu zdvojovalo).
 * `system` nemá legacy top-level protějšek → vznikne z něj native-only sekce.
 */
const SECTION = {
  general: 'general',
  inventory: 'inventory',
  products: 'products',
  reservations: 'reservations',
  www: 'www',
  system: 'system',
} as const;

/** Stabilní ID vnořených skupin uvnitř sekcí (shodné s Magic `<SubItem ID>`). */
const GROUP = {
  inventoryCodebooks: 'inventory-codebooks',
} as const;

/**
 * Baseline (native) položky hlavního menu.
 *
 * Položky se `section` se mergují do stejnojmenné root sekce (Phoenix native ↔ legacy menu);
 * ve fázi 1 se vkládají na začátek sekce. Položky bez `section` zůstávají samostatným tlačítkem
 * vlevo v liště. `section`/`group` = stabilní ID (viz SECTION/GROUP). `sectionLabel`/`groupLabel` =
 * lokalizovaný popisek použitý JEN u native-only sekce/skupiny (bez legacy protějšku); jinak se
 * zobrazí Magicem lokalizovaný label legacy sekce.
 */
export function getMainMenuItems(t: TFunction): readonly MainMenuItem[] {
  const items: MainMenuItem[] = [
    standardMenuItem('venua-planner', t('menu.venuaPlanner'), '/venua-planner', 20, {
      section: SECTION.products,
      sectionLabel: t('menu.sections.products'),
      permission: VENUA_PLANNER_PERMISSION,
    }),
    standardMenuItem('columbus-planner', t('menu.columbusPlanner'), '/columbus-planner', 30, {
      section: SECTION.products,
      sectionLabel: t('menu.sections.products'),
      permission: COLUMBUS_PERMISSION,
    }),
    // Nahrazuje legacy Vehicle.List, ať agenda není v menu dvakrát.
    standardMenuItem('inventory-vehicles', t('menu.vehicles'), '/inventory-vehicles', 34, {
      section: SECTION.inventory,
      sectionLabel: t('menu.sections.inventory'),
      replacesKey: 'Vehicle.List',
      permission: INVENTORY_VEHICLES,
    }),
    // Sekce „Inventář" › skupina „Číselníky" — číselník leteckých společností mezi ostatní
    // číselníky (Stanice, Trasy, placeholdery). Legacy protějšek nemá, proto bez `replacesKey`
    // (nenahrazuje žádnou položku legacy menu Magicu).
    standardMenuItem('inventory-airlines', t('menu.airlines'), '/inventory-airlines', 39, {
      section: SECTION.inventory,
      sectionLabel: t('menu.sections.inventory'),
      group: GROUP.inventoryCodebooks,
      groupLabel: t('menu.groups.codebooks'),
      permission: INVENTORY_AIRLINES,
    }),
    // Sekce „Inventář" › skupina „Číselníky" — agenda Stanice (merge do stejnojmenné legacy skupiny).
    standardMenuItem('stations', t('menu.stations'), '/products', 35, {
      section: SECTION.inventory,
      sectionLabel: t('menu.sections.inventory'),
      group: GROUP.inventoryCodebooks,
      groupLabel: t('menu.groups.codebooks'),
      permission: PRODUCTS_STATIONS,
    }),
    // Sekce „Inventář" › skupina „Číselníky" — agenda Trasy autobusů.
    standardMenuItem('station-chains', t('menu.stationChains'), '/products-chains', 36, {
      section: SECTION.inventory,
      sectionLabel: t('menu.sections.inventory'),
      group: GROUP.inventoryCodebooks,
      groupLabel: t('menu.groups.codebooks'),
      permission: PRODUCTS_STATION_CHAINS,
    }),
    // Sekce „Inventář" › skupina „Číselníky" — Knihovna placeholderů.
    standardMenuItem('placeholders', t('menu.placeholders'), '/placeholders', 37, {
      section: SECTION.inventory,
      sectionLabel: t('menu.sections.inventory'),
      group: GROUP.inventoryCodebooks,
      groupLabel: t('menu.groups.codebooks'),
      permission: VENUA_PLANNER_PLACEHOLDERS,
    }),
    // Sekce „Inventář" › skupina „Číselníky" — Subkategorie placeholderů.
    standardMenuItem(
      'placeholder-subcategories',
      t('menu.placeholderSubcategories'),
      '/placeholder-subcategories',
      38,
      {
        section: SECTION.inventory,
        sectionLabel: t('menu.sections.inventory'),
        group: GROUP.inventoryCodebooks,
        groupLabel: t('menu.groups.codebooks'),
        permission: VENUA_PLANNER_PLACEHOLDER_SUBCATEGORIES,
      }
    ),
    // Sekce „Rezervace" — merguje se se stejnojmennou legacy sekcí a NAHRAZUJE legacy agendu
    // „Obchodní případy" (`BusinessCase.List`), aby v menu nebyla dvakrát.
    standardMenuItem('reservations', t('menu.reservations'), '/reservations', 40, {
      section: SECTION.reservations,
      sectionLabel: t('menu.sections.reservations'),
      replacesKey: 'BusinessCase.List',
      permission: RESERVATIONS_BUSINESS_CASES,
    }),
    // Sekce „WWW" — filtr zájezdů (merge do legacy sekce www).
    standardMenuItem('web-filters', t('menu.webFilters'), '/web-filters', 50, {
      section: SECTION.www,
      sectionLabel: t('menu.sections.www'),
      permission: WWW_WEB_FILTERS,
    }),
    // Sekce „Systém" — legacy nemá top-level protějšek → vznikne samostatná native-only sekce.
    standardMenuItem('security', t('menu.security'), '/security', 90, {
      section: SECTION.system,
      sectionLabel: t('menu.sections.system'),
      permission: SECURITY_ROLE_PERMISSIONS,
    }),
  ];

  // Design showcase je vývojářský nástroj — v menu jen na dev prostředí.
  if (getRuntimeConfig().app.environment === 'dev') {
    items.push(
      standardMenuItem('design', t('menu.design'), '/design', 40, {
        section: SECTION.general,
        sectionLabel: t('menu.sections.general'),
      })
    );
  }

  return items;
}
